import { DateTime } from 'luxon';
import { useState } from 'react';
import { Button } from '../../components/button';
import {
  Alert,
  Field,
  Input,
  InlineRow,
  LoadingBlock,
  Muted,
  PageContainer,
  PageHeader,
  SectionHeader,
  Stack,
} from '../../components/ui';
import { useProviderTodayQuery } from '../../services/serviceService/__generated__/queries.generated';
import { stayErrorMessage } from '../StayHostPage/stayErrors';
import { ProviderJobCard } from './ProviderJobCard';

const SECTIONS = [
  { key: 'requests', title: 'Requests', empty: 'No requests waiting for you.' },
  { key: 'today', title: 'Today', empty: 'No jobs on this day.' },
  { key: 'inProgress', title: 'In progress', empty: 'No worker is on a job now.' },
  {
    key: 'awaitingConfirmation',
    title: 'Waiting for the customer',
    empty: 'No finished jobs waiting.',
  },
  { key: 'upcoming', title: 'Next 14 days', empty: 'Nothing booked yet.' },
] as const;

/**
 * The Provider's home on the services site (spec 019 W17, W18): requests to
 * accept or decline, the day's jobs, jobs in progress, finished jobs waiting
 * for the customer, and the next 14 days. `providerToday(date)`; no date is
 * the Provider's today.
 */
export default function ProviderTodayPage() {
  const [date, setDate] = useState('');
  const query = useProviderTodayQuery({
    variables: { date: date || null },
    fetchPolicy: 'cache-and-network',
    pollInterval: 60000,
  });
  const data = query.data?.providerToday;
  return (
    <PageContainer width="lg" canvas data-testid="provider-today">
      <PageHeader
        eyebrow="Provider"
        title={date ? DateTime.fromISO(date).toFormat('cccc, LLL d') : 'Today'}
        actions={
          <InlineRow>
            <Field label="Day" htmlFor="today-date">
              <Input
                id="today-date"
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
              />
            </Field>
            {date && (
              <Button
                type="button"
                theme="text"
                size="sm"
                text="Today"
                onClick={() => setDate('')}
              />
            )}
          </InlineRow>
        }
      />
      {query.loading && !data ? (
        <LoadingBlock label="Loading your jobs" />
      ) : query.error && !data ? (
        <Alert tone="error" title="Could not load your jobs">
          {stayErrorMessage(query.error)}
        </Alert>
      ) : data ? (
        <Stack $gap={8}>
          {SECTIONS.map(section => {
            const jobs = data[section.key];
            return (
              <section key={section.key} data-testid={`today-${section.key}`}>
                <SectionHeader
                  title={`${section.title}${jobs.length ? ` (${jobs.length})` : ''}`}
                />
                {jobs.length === 0 ? (
                  <Muted $size="sm">{section.empty}</Muted>
                ) : (
                  <Stack $gap={4}>
                    {jobs.map(job => (
                      <ProviderJobCard key={job.id} job={job} />
                    ))}
                  </Stack>
                )}
              </section>
            );
          })}
        </Stack>
      ) : null}
    </PageContainer>
  );
}
